import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import isEqual from "lodash.isequal";
import React, {useEffect, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {NICKNAME_MAX_LENGTH} from "@khan/auth-password";
import {useMutation} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";
import {
    isKhanAcademyUrl,
    preserveMarketingParams,
    redirect,
    useLocation,
} from "@khan/urls";

import type {SetBasicSettingsType} from "./__graphql-types__/index.ts";
import {SET_BASIC_SETTINGS_MUTATION} from "./basic-settings-mutation.ts";
import {Section} from "./components/section/section.tsx";
import RegionSelector from "./region-selector.tsx";
import {SaveChangesButton} from "./save-changes-button.tsx";
import type {BasicSettingsData} from "./types/account-settings-types.ts";

interface Props {
    targetIsChild: boolean;
    initialSettings: BasicSettingsData;
    kaid: string;
    successfullyChangedSettings: () => unknown;
    includesCentrallyOwnedDistrictData: boolean;
    targetIsTeacher: boolean;
}

const BasicSettings: React.FC<Props> = ({
    targetIsChild,
    initialSettings,
    kaid,
    successfullyChangedSettings,
    includesCentrallyOwnedDistrictData,
    targetIsTeacher,
}) => {
    const location = useLocation();
    const queryParams = location.queryParams;
    const [showErrors, setShowErrors] = useState(true);
    const [nicknameError, setNicknameError] = useState<string | null>(null);
    const [reauthError, setReauthError] = useState<string | null>(null);
    const [isUnexpectedError, setIsUnexpectedError] = useState(false);
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [currentSettings, setCurrentSettings] =
        useState<BasicSettingsData>(initialSettings);
    const [isMounted, setIsMounted] = useState(false);
    const [setBasicSettingsMutation] = useMutation(SET_BASIC_SETTINGS_MUTATION);
    // We need to keep track of isMounted so that we only
    // set the state after the storeBasicSettings promise
    // resolves IF the component is mounted.
    useEffect(() => {
        setIsMounted(true);
        return () => {
            setIsMounted(false);
        };
    }, []);

    const handleSettingsChange = (newSettings: Partial<BasicSettingsData>) => {
        setShowErrors(false);
        setCurrentSettings((prevSettings) => ({
            ...prevSettings,
            ...newSettings,
        }));
    };

    const storeSettingsAndModifyState = async (
        currentSettings: BasicSettingsData,
    ) => {
        let result: Result<SetBasicSettingsType["response"]> | null;
        try {
            result = await setBasicSettingsMutation({...currentSettings, kaid});
        } catch (e) {
            throw new KAError("Failed to set basic settings", Errors.Internal, {
                cause: e as Error,
            });
        }
        if (result.status === "success") {
            const errors = result.data.setSettings?.errors || [];
            const errorCodes = errors.map((e) => e?.code);

            if (errorCodes.length > 0) {
                if (errorCodes.includes("NICKNAME_ERROR")) {
                    setNicknameError(t`Please pick a different name.`);
                }
                if (errorCodes.includes("REAUTH_REQUIRED")) {
                    setReauthError(
                        t`Reauthentication required to perform this action. Please try again.`,
                    );
                }
                return;
            }
        }

        if (queryParams.continue && isKhanAcademyUrl(queryParams.continue)) {
            redirect(
                preserveMarketingParams(
                    queryParams.continue,
                    new URLSearchParams(location.search),
                ),
            );
            return;
        }

        successfullyChangedSettings();
    };

    const setSettings = async (currentSettings: BasicSettingsData) => {
        setIsSavingSettings(true);
        setNicknameError(null);
        setReauthError(null);

        try {
            await storeSettingsAndModifyState(currentSettings);
            if (isMounted) {
                setIsSavingSettings(false);
            }
        } catch {
            setIsUnexpectedError(true);
            setIsSavingSettings(false);
        }
    };

    const setSettingsAndClearErrors = () => {
        const newSettings = {...currentSettings};

        // If the nickname and/or username has been changed, ask for
        // confirmation first. If the user does not OK the alert,
        // reset the field.
        if (
            initialSettings.nickname !== currentSettings.nickname &&
            // TODO:  (maggieliu) this should be a modal, not an alert
            // eslint-disable-next-line no-alert -- allow alerts
            !confirm(t`Are you sure you want to change your nickname?`)
        ) {
            newSettings.nickname = initialSettings.nickname;
        }

        // Even though this is an async function, we don't await it;
        // we let it run in the background and update state when it's
        // finished.
        setSettings(newSettings);
        setShowErrors(true);
        setCurrentSettings(newSettings);
    };

    const settings = {
        ...initialSettings,
        ...currentSettings,
    };

    const {nickname, region} = settings;

    const targetIsOnEnglishSiteInUS =
        RequestInfo.kaLocale === "en" && RequestInfo.remoteCountry === "US";

    return (
        <Section
            legendContent={t`Basics`}
            children={
                <View>
                    {/** Real name / nickname */}
                    {!targetIsChild && (
                        <View style={styles.sectionitem}>
                            <LabeledField
                                field={
                                    <TextField
                                        testId="settings-nickname"
                                        id="nickname"
                                        value={nickname || ""}
                                        onChange={(newValue) =>
                                            handleSettingsChange({
                                                nickname: newValue.substring(
                                                    0,
                                                    NICKNAME_MAX_LENGTH,
                                                ),
                                            })
                                        }
                                        validate={() => nicknameError}
                                        disabled={
                                            // A district student rostered via the teacher-directed
                                            // mechanism should have their name be editable by the teacher
                                            // (i.e., not disabled).
                                            includesCentrallyOwnedDistrictData &&
                                            !targetIsTeacher
                                        }
                                        style={styles.nicknameField}
                                    />
                                }
                                label={t`Name`}
                                description={t`This is how your name will appear around Khan Academy, and how your friends and coaches will recognize you.`}
                            />
                        </View>
                    )}
                    {/** Region */}
                    {targetIsOnEnglishSiteInUS && (
                        <RegionSelector
                            selectedRegionCode={region}
                            onChange={(region) =>
                                handleSettingsChange({region})
                            }
                        />
                    )}
                </View>
            }
            footer={
                <SaveChangesButton
                    setSettings={setSettingsAndClearErrors}
                    isLoading={isSavingSettings}
                    isDisabled={
                        isSavingSettings ||
                        isEqual(initialSettings, currentSettings)
                    }
                    hasUnexpectedError={showErrors && isUnexpectedError}
                    maybeReauthError={
                        !showErrors || !reauthError ? reauthError : null
                    }
                />
            }
            footerStyle={styles.footer}
        />
    );
};

export default React.memo(BasicSettings);

const styles = StyleSheet.create({
    nicknameField: {
        maxInlineSize: 400,
    },
    sectionitem: {
        marginBlockEnd: sizing.size_160,
    },
    footer: {
        marginBlockStart: 0,
    },
});
